import { AI_TOOL } from "../types";

const OUTPUT_BLOCK_TYPES = [
  "bash_code_execution_output",
  "code_execution_output"
];

/**
 * The streaming placeholder already carries the file id, but the backend only
 * writes the assistant message once the stream closes, and the download
 * endpoint authorizes against that stored message. Resolving a file from an
 * unpersisted message therefore fails with "File not found."
 */
export const isPersistedMessage = (m: any): boolean =>
  typeof m?._id === "string" && !m._id.startsWith("temp_");

export const collectContainerFileIds = (content: any[]): string[] => {
  const ids: string[] = [];

  for (const block of content ?? []) {
    const outs = block?.content?.content;
    if (!Array.isArray(outs)) continue;

    for (const o of outs) {
      if (o?.file_id && OUTPUT_BLOCK_TYPES.includes(o.type)) {
        ids.push(o.file_id);
      }
    }
  }

  return ids;
}

export const findContainerFileId = (content: any[]): string | null => {
  const ids = collectContainerFileIds(content);
  return ids.length ? ids[ids.length - 1] : null;
}

export const hasAnalysisSubmit = (content: any[]): boolean => (content ?? []).some(
  b => b?.type === "tool_use" && b?.name === AI_TOOL.SUBMIT_ANALYSIS_RESULT
);

const isAssistantMessage = (m: any): boolean =>
  m?.role === "assistant" && Array.isArray(m?.content);

/**
 * A client tool call ends the assistant turn, so the code execution command that
 * wrote the file and the tool call that delivers it regularly land in different
 * messages. Walk backwards from `before` (exclusive) and take the newest output.
 */
export const findContainerFileIdInHistory = (messages: any[], before?: number): string | null => {
  const start = Math.min(before ?? messages?.length ?? 0, messages?.length ?? 0);

  for (let i = start - 1; i >= 0; i--) {
    if (!isAssistantMessage(messages[i])) continue;

    const id = findContainerFileId(messages[i].content);
    if (id) return id;
  }

  return null;
}

/** The newest container output at `index`, else the newest one written before it. */
export const resolveContainerFileId = (messages: any[], index: number): string | null =>
  findContainerFileId(messages?.[index]?.content ?? [])
  ?? findContainerFileIdInHistory(messages, index);
